// Shared largest-remainder (Hamilton) apportionment used by both `allocation.ts`
// (§9.3, landed cost) and `absorption.ts` (§9.4, co-product split) — the same
// conservation-critical primitive, kept in one place so the two seams can never
// drift apart on rounding behaviour.
/**
 * Apportions a non-negative `total` across `items` by non-negative integer
 * `weight`. Guarantees the returned amounts sum exactly to `total`. Ties in the
 * fractional remainder are broken by `tieBreak` (largest wins), then by original
 * input order, so results are deterministic.
 */
export function largestRemainderAllocate(total, items) {
    const totalWeight = items.reduce((sum, item) => sum + item.weight, 0n);
    if (totalWeight === 0n) {
        // No weight to allocate by — split evenly (used only when every item is zero-weight).
        return largestRemainderAllocate(total, items.map((item) => ({ ...item, weight: 1n })));
    }
    const shares = items.map((item, index) => {
        const numerator = total * item.weight;
        const floorCents = numerator / totalWeight; // bigint division truncates toward zero; safe since both operands >= 0
        const remainder = numerator % totalWeight; // in [0, totalWeight)
        return { id: item.id, floorCents, remainder, tieBreak: item.tieBreak, index };
    });
    const result = new Map();
    for (const share of shares) {
        result.set(share.id, share.floorCents);
    }
    let deficit = total - shares.reduce((sum, s) => sum + s.floorCents, 0n);
    const order = [...shares].sort((a, b) => {
        if (a.remainder !== b.remainder)
            return a.remainder > b.remainder ? -1 : 1;
        if (a.tieBreak !== b.tieBreak)
            return a.tieBreak > b.tieBreak ? -1 : 1;
        return a.index - b.index;
    });
    for (let i = 0; i < order.length && deficit > 0n; i += 1, deficit -= 1n) {
        const current = order[i];
        if (!current)
            break;
        result.set(current.id, (result.get(current.id) ?? 0n) + 1n);
    }
    return result;
}
//# sourceMappingURL=largest-remainder.js.map